import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../Head';
import Hero from '../Hero';
import * as css from './Development.m.css';
import * as commonCss from '../../Common.m.css';
import Summary from '../Summary';
import ConnectStrip from '../ConnectStrip';
import FAQ from '../FAQ';
import IndustryExpertise from '../IndustryExpertise';

const sitepenLogo = require('../../assets/images/logos/sitepen.svg');
const engineeringImg = require('../../assets/images/development/engineering.svg');
const softwareCycleImg = require('../../assets/images/development/software-cycle.svg');

const softwareImg = require('../../assets/images/development/software-consulting-design.svg');
const customImg = require('../../assets/images/development/custom-software.svg');
const legacyImg = require('../../assets/images/development/legacy-software-modernization.svg');
const frontEndAppImg = require('../../assets/images/development/front-end-web-app.svg');
const backEndServicesImg = require('../../assets/images/development/back-end-services.svg');
const cloudImg = require('../../assets/images/development/cloud-infrastructure.svg');
const databaseDevImg = require('../../assets/images/development/database-development.svg');

const factory = create();

export const CustomSoftwareDevelopment = factory(function CustomSoftwareDevelopment() {
	return (
		<div classes={css.root}>
			<Head
				title="Custom Software Development Services | SitePen"
				description="Custom software development company building enterprise solutions to improve processes, streamline operations, and empower business success."
			/>
			<div classes={[css.heroWrapper]}>
				<div classes={commonCss.contentWrapper}>
					<Hero>
						{{
							xlTitle: 'Custom Software Development'
						}}
					</Hero>
				</div>
			</div>
			<section classes={commonCss.section}>
				<div classes={commonCss.contentWrapper}>
					<Summary reverse collapse="hide" margin={false}>
						{{
							title: 'Build for Innovation ',
							blurb: (
								<virtual>
									<p>
										In today's world, every enterprise is driven by software.
										Through a mix of off-the-shelf and custom applications, each
										organization builds a virtual world that supports their
										goals and objectives. In these worlds, pre-packaged
										applications provide a known, reliable foundation. These are
										vital components, but insufficient to address the unique
										challenges and goals of each enterprise. Custom software
										energizes this virtual world, granting it the ability to
										adapt as markets evolve and business needs change.
									</p>
									<p>
										SitePen offers a wide range of custom software services that
										are tailored to meet the different challenges that
										enterprises face. Whether entering new markets or expanding
										existing ones, new applications and services often need to
										be integrated into the current software ecosystem. Our
										design and consulting services are ideal for identifying and
										refining system requirements and developing robust
										person-machine interfaces. As applications age, they often
										become encumbered with outdated technologies, and their code
										base's structure begins to degrade. SitePen's engineering
										team has extensive experience in enterprise technologies,
										both past, and present. This knowledge allows them to
										develop clear designs that can breathe new life into legacy
										systems.
									</p>
								</virtual>
							),
							image: (
								<img
									src={engineeringImg}
									classes={commonCss.responsiveImage}
									alt=""
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={[commonCss.shaded, commonCss.section]}>
				<div classes={commonCss.contentWrapper}>
					<h2>Custom Software Development Services</h2>
					<p>
						Custom software is the vehicle that supports the unique challenges of
						fulfilling your user's desire to obtain your products or services. We will
						partner with you to learn how your business works, and then we will work
						with you to transform those processes into efficient and maintainable
						software.
					</p>
					<ul classes={css.columns}>
						<li classes={css.column}>
							<img
								classes={css.columnImage}
								src={softwareImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={css.columnTitle}>Software Consulting &amp; Design</h3>
							<p>
								Many enterprises struggle to mesh business needs with software
								engineering capabilities. Our extensive experience in a wide range
								of industries makes us a perfect partner when developing software
								strategies that provide high-value solutions. At the tactical level,
								our design team's expertise is an invaluable resource when designing
								new application interfaces that are efficient, error-resistant, and
								intuitive.
							</p>
						</li>
						<li classes={css.column}>
							<img classes={css.columnImage} src={customImg} alt="" loading="lazy" />
							<h3 classes={css.columnTitle}>End-to-end Software Development</h3>
							<p>
								SitePen's engineering teams monitor the software development
								community's ever-changing landscape. We bring this knowledge to all
								aspects of our projects, from the user-interface to the database and
								back. This understanding ensures that every part of the software
								solution reflects best-practices and is optimized, both as
								individual components and as a holistic system.
							</p>
						</li>
						<li classes={css.column}>
							<img classes={css.columnImage} src={legacyImg} alt="" loading="lazy" />
							<h3 classes={css.columnTitle}>Legacy Software Modernization</h3>
							<p>
								Nothing lasts forever, especially software developed in an
								ever-changing market. We understand the challenges and opportunities
								that legacy software modernization projects bring. We believe that
								most applications can have several years added to their useful
								lifespans through careful analysis and strategic updates that keep
								most of the application intact while supporting evolving business
								objectives.
							</p>
						</li>
					</ul>
				</div>
			</section>
			<IndustryExpertise />
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.contentWrapper}>
					<h2>Technologies We Use</h2>
					<p>
						Enterprise applications are composed of a complex set of interconnected
						systems that must work smoothly together. At SitePen, we have expertise
						throughout the entire development stack, including front-end web
						applications, back-end web services and applications, cloud-based
						infrastructure, and database development and optimization.
					</p>
					<ul classes={[css.columns, css.mediumTwoColumns]}>
						<li classes={css.column}>
							<img
								classes={css.columnImage}
								src={frontEndAppImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={css.columnTitle}>Front-end Web Applications</h3>
							<p>JavaScript, TypeScript, Angular, React, Vue, Dojo</p>
						</li>
						<li classes={css.column}>
							<img
								classes={css.columnImage}
								src={backEndServicesImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={css.columnTitle}>Back-end Web Services</h3>
							<p>Node.js, Java, PHP</p>
						</li>
						<li classes={css.column}>
							<img classes={css.columnImage} src={cloudImg} alt="" loading="lazy" />
							<h3 classes={css.columnTitle}>Cloud-based Infrastructure</h3>
							<p>AWS, Azure, Google Cloud</p>
						</li>
						<li classes={css.column}>
							<img
								classes={css.columnImage}
								src={databaseDevImg}
								alt=""
								loading="lazy"
							/>
							<h3 classes={css.columnTitle}>Database Development</h3>
							<p>MySQL, MariaDB, MongoDB, RavenDB, PostgreSQL</p>
						</li>
					</ul>
				</div>
			</section>
			<section classes={[commonCss.largePaddingTop, commonCss.smallContentWrapper]}>
				<h2>Choosing the Right Software Development Company</h2>
				<p>
					There are many options to choose from when it comes to picking a software
					development company. Many of those companies focus on telling you what they do
					well. At SitePen, we believe that the ideal company for you to work with is one
					that listens first. We want to get to know you, your company, and your needs. As
					we learn more about you, we can offer the help and solutions that are most
					beneficial to you.
				</p>
				<div classes={commonCss.centeredTextBreakImage}>
					<img src={sitepenLogo} alt="SitePen" loading="lazy" />
				</div>
				<h2>Why Develop with SitePen</h2>
				<p>
					When SitePen was founded almost 20 years ago, it had three characteristics that
					have made it the ideal partner for many enterprises. Its founders believed that
					front-end internet applications were the future, its employees worked 100%
					remotely, and it was firmly committed to the open-source community.
				</p>
				<p>
					Today, it is assumed that any web application will contain a significant amount
					of client-side functionality. Twenty years ago, things were less clear.
					JavaScript support was inconsistent and slow and simply getting two web browsers
					to exhibit the same behavior was challenging. Despite these problems, SitePen
					understood the rich rewards that a successful front-end application could yield
					in terms of user satisfaction and efficiency. We continue to look at the latest
					technology trends, applying the best ideas to maximize your
					return-on-investment.
				</p>
				<p>
					In the early 2000s, almost every company had a physical office. Our founders
					realized that operating in a remote-only fashion promised dramatic advantages if
					it could be made to work. This decision has allowed SitePen to hire the most
					talented engineers, designers, and project managers from around the world. We
					have also developed robust communications channels to share information with
					clients as transparently as we do internally.
				</p>
				<p>
					Another characteristic that makes SitePen a valuable partner is its commitment
					to the open-source community. Our engineering teams continuously hone their
					skills by supporting the development of large projects, such as{' '}
					<a target="_blank" rel="noopener" href="https://dojo.io">
						Dojo
					</a>{' '}
					and{' '}
					<a target="_blank" rel="noopener" href="https://theintern.io">
						The Intern
					</a>
					. Open-source projects also allow us to learn from other contributors, keeping
					our teams engaged with a broad cross-section of the software development
					community.
				</p>
			</section>
			<section classes={[commonCss.section, commonCss.smallContentWrapper]}>
				<img
					src={softwareCycleImg}
					classes={commonCss.centeredHeaderImage}
					alt=""
					loading="lazy"
				/>
				<h2>Our Software Development Process</h2>
				<p>
					Our development partnership with you starts the moment you reach out to us. Our
					solution architects will work with you to understand your goals and gather the
					background information that informs our entire development process. Our project
					management, senior designers, and technical leads will take this information and
					develop high-level project plans before you even see a contract. We will then
					work with you to ensure that we agree upon a set of clear, achievable goals.
					Throughout the development process, we stay in constant contact with you. As we
					all learn more about the project, we'll find areas that need to be refined and
					changed. We will work with you to integrate these changes into the plan so that
					the software reflects all of the lessons we learned during its development.
				</p>
			</section>
			<ConnectStrip />
			<FAQ title="Custom Software Development FAQs">
				{[
					{
						title: 'What Is Custom Software Development?',
						content: (
							<p>
								Custom Software Development is the practice of developing software
								systems that are unique to a business or enterprise. They are
								usually designed to automate business processes to increase the
								efficiency and effectiveness of an organization.
							</p>
						)
					},
					{
						title: `Why Custom Software Development Is Important?`,
						content: (
							<p>
								All companies have access to the same off-the-shelf solutions. While
								these packages can provide a strong foundation for a company's
								software ecosystem, they are not optimized to address each
								organization's unique needs. Custom software provides the "glue"
								that binds pre-packaged solutions together in a way that maximizes
								ROI by offering solutions that automate many aspects of a company's
								business processes.
							</p>
						)
					},
					{
						title: 'How Much Does Custom Software Development Cost?',
						content: (
							<p>
								Due to each solution's unique nature, custom software is often the
								most expensive way to bring new software into a company. These costs
								can be dramatically reduced by limiting the scope of custom
								development projects to provide only those features that are not
								available from off-the-shelf solutions. These reductions are
								typically realized by integrating existing solutions into the custom
								software project. Additional cost benefits can be gained by
								partnering with a company, like SitePen, with extensive experience
								with this kind of development.
							</p>
						)
					}
				]}
			</FAQ>
		</div>
	);
});

export default CustomSoftwareDevelopment;
